import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { fetchSurveyResult } from '../redux/actions';
import { ScaleCard, OptionCard, NoResultCard } from '../components/results';
import { TEXT, MESSAGE } from '../constants/question_types';
import { hasMatchedCondition } from '../helpers/logic_helper';
import { useAppDispatch, useAppSelector } from '../redux/ReduxProvider';

const RESULT_TYPES = {
  TEXT: 'text',
  SCALE: 'scale',
  OPTION: 'option',
};

const useStyles = makeStyles((theme) => ({
  messageContainer: {
    marginTop: 16,
    marginBottom: 16,
    textAlign: 'center',
  },
  message: {
    fontSize: 32,
    color: theme.palette.mainaccent,
  },
  card: {
    padding: 8,
  },
}));

const ResultsPage = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const titleRef = useRef();

  const { id, surveyCode, results } = useAppSelector((state) => state.survey);
  const questions = useAppSelector((state) => state.questions);
  const answers = useAppSelector((state) => state.answers);

  const visibleQuestions = questions.filter((question) => {
    if (!question.displayConditions || question.displayConditions.length <= 0) return true;
    return hasMatchedCondition(question.displayConditions, answers);
  });

  useEffect(() => {
    dispatch(fetchSurveyResult(surveyCode));
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (titleRef?.current) {
      titleRef.current.focus();
    }
  }, [titleRef]);

  const filteredResult = results?.filter((result) => {
    if (result.questionType === TEXT || result.questionType === MESSAGE) return false;
    const isVisible = !!visibleQuestions.find((q) => q.id === result.questionId);
    return isVisible;
  });

  const renderResult = (result) => {
    if (!result.result) {
      return (
        <NoResultCard
          key={result.questionId}
          {...result}
          translation={{
            noEnoughData: t('graph.notEnough.header'),
          }}
        />
      );
    }

    const translation = {
      allRespondents: t('showing.all'),
      comments: t('result.comments'),
      outOf: t('tableview.outof'),
      peopleResponded: t('peopleResponded'),
      eNPS: t('admin.question.nps'),
      avg: t('result.avg'),
      positive: t('result.positive'),
      attributeBased: t('survey.type.attributebased'),
    };

    let Card;
    switch (result.result.type) {
      case RESULT_TYPES.SCALE:
        Card = ScaleCard;
        break;
      case RESULT_TYPES.OPTION:
        Card = OptionCard;
        break;
      default:
        return null;
    }
    return <Card key={result.questionId} translation={translation} {...result} showHoverover={false} />;
  };

  return (
    <Grid container role="main" id="main">
      <Grid ref={titleRef} className={styles.messageContainer} item xs={12} tabIndex={0}>
        <Typography className={styles.message} variant="h1" aria-label={t('survey.thanks.greeting')}>
          {t('survey.thanks.greeting')}
        </Typography>
        <Typography>{t('survey.thanks.message')}</Typography>
      </Grid>
      {filteredResult &&
        filteredResult.map((result) => (
          <Grid key={result.questionId} className={styles.card} item xs={12} sm={6} md={4} lg={3} xl={2}>
            {renderResult(result)}
          </Grid>
        ))}
    </Grid>
  );
};

export default ResultsPage;
